import { useEffect, useMemo, useState } from "react";
import { Search, ArrowLeftRight, AlertTriangle, CircleCheck } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { getQuoteHomeWarnings } from "@/lib/homeCatalogCompleteness";
import { useAuth } from "@/lib/auth";

function parseNumber(value) {
    const number = parseFloat(value || 0);
    return Number.isFinite(number) ? number : 0;
}

function calculatorPerimeter(dimensions) {
    if (!dimensions) return null;
    const text = String(dimensions)
        .toLowerCase()
        .replace(/[′’']/g, "")
        .replace(/[″”"]/g, "")
        .replace(/ft\.?/g, "")
        .replace(/feet/g, "");
    const match = text.match(/(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;
    const first = Number(match[1]);
    const second = Number(match[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;
    return Math.round((first + second) * 2 * 100) / 100;
}

function getNormalizedRole(...users) {
    const role = users.find((candidate) => candidate?.role)?.role;
    return String(role || "").trim().toLowerCase();
}

function isManagerOrAdminRole(role) {
    return ["admin", "manager"].includes(role);
}

function canEditQuotePricingRole(role) {
    return ["admin", "manager", "salesperson", "sales", "quote", "quote_user", "sales_quote", "associate"].includes(role);
}

function homeTypeHelperText(source) {
    if (source === "catalog") return "Auto-filled from selected home. Override only if needed.";
    if (source === "inferred") return "Auto-filled from selected home dimensions/width. Override only if needed.";
    return "Select manually if home type cannot be inferred.";
}

export default function StepHome({ home, onSelect, basePrice, factoryCost, calculatedEhsPrice, msrpCalculated, priceOverridden, overrideReason, update, currentUser }) {
    const { user } = useAuth() || {};
    const [manufacturers, setManufacturers] = useState([]);
    const [manufacturer, setManufacturer] = useState("all");
    const [series, setSeries] = useState("all");
    const [q, setQ] = useState("");
    const [bedsFilter, setBedsFilter] = useState("all");
    const [bathsFilter, setBathsFilter] = useState("all");
    const [homeTypeFilter, setHomeTypeFilter] = useState("all");
    const [sortBy, setSortBy] = useState("price_asc");
    const [homes, setHomes] = useState([]);
    const [customMode, setCustomMode] = useState(false);
    const hasPendingPriceOverride = calculatedEhsPrice > 0 && Math.abs(Number(basePrice || 0) - Number(calculatedEhsPrice || 0)) >= 0.005;

    useEffect(() => {
        api.get("/homes/manufacturers").then((r) => setManufacturers(r.data));
    }, []);

    const loadHomes = () => {
        const params = {};
        if (manufacturer && manufacturer !== "all") params.manufacturer = manufacturer;
        if (series && series !== "all") params.series = series;
        if (q) params.q = q;
        params.limit = 50;
        api.get("/homes", { params }).then((r) => setHomes(r.data));
    };
    useEffect(loadHomes, [manufacturer, series]);

    const seriesList = useMemo(() => {
        if (manufacturer === "all") return [];
        const m = manufacturers.find((mm) => mm.manufacturer === manufacturer);
        return m?.series || [];
    }, [manufacturers, manufacturer]);

    const updateSelectedHome = (patch) => {
        update({ home: { ...(home || {}), ...patch } });
    };

    const perimeter = calculatorPerimeter(home?.dimensions);

    const filteredHomes = useMemo(() => {
        const search = q.trim().toLowerCase();
        const rows = homes.filter((h) => {
            if (search) {
                const haystack = [h.model_name, h.manufacturer, h.series].filter(Boolean).join(" ").toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            if (bedsFilter !== "all" && String(h.beds ?? "") !== bedsFilter) return false;
            if (bathsFilter !== "all" && String(h.baths ?? "") !== bathsFilter) return false;
            if (homeTypeFilter !== "all" && String(h.home_type || "").toLowerCase() !== homeTypeFilter.toLowerCase()) return false;
            return true;
        });

        const sorted = [...rows].sort((a, b) => {
            const priceA = Number(a.ehs_price || 0);
            const priceB = Number(b.ehs_price || 0);
            const sqftA = Number(a.sqft || 0);
            const sqftB = Number(b.sqft || 0);
            if (sortBy === "price_desc") return priceB - priceA;
            if (sortBy === "sqft_asc") return sqftA - sqftB;
            if (sortBy === "sqft_desc") return sqftB - sqftA;
            return priceA - priceB;
        });

        return sorted;
    }, [homes, q, bedsFilter, bathsFilter, homeTypeFilter, sortBy]);


    const homeWarnings = useMemo(() => getQuoteHomeWarnings(home || {}), [home]);
    const role = getNormalizedRole(currentUser, user);
    const isManagerOrAdmin = isManagerOrAdminRole(role);
    const canEditQuotePricing = canEditQuotePricingRole(role);
    const quotePricingDisabledTitle = canEditQuotePricing ? undefined : "Your role cannot edit quote-specific pricing.";
    const canEditCatalog = isManagerOrAdmin;

    const homeTypes = useMemo(() => {
        const set = new Set(homes.map((h) => h.home_type).filter(Boolean));
        return Array.from(set).sort();
    }, [homes]);

    return (
        <div className="space-y-4" data-testid="step-home">
            {home?.model_name && !customMode && (
                <Card className="rounded-xl border-[#1E6FA8] bg-[rgba(67,161,215,0.05)] p-5">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-[#1E6FA8]">Selected home</div>
                            <div className="font-display text-2xl text-slate-900 mt-1">{home.model_name}</div>
                            <div className="text-sm text-slate-600">{home.manufacturer} · {home.series}</div>
                            <div className="mt-2 text-xs text-slate-500">
                                {home.beds || "?"} bd · {home.baths || "?"} ba · {home.sqft ? `${home.sqft} sqft` : ""} · {home.dimensions || "dimensions needed"}
                            </div>
                        </div>
                        <Button variant="outline" onClick={() => onSelect(null)} data-testid="step-home-change-button">
                            <ArrowLeftRight className="mr-2 h-4 w-4" /> Change
                        </Button>
                    </div>
                </Card>
            )}

            
            {home?.model_name && (
                <Card className={homeWarnings.warnings.length ? "rounded-xl border-amber-200 bg-amber-50/50 p-4" : "rounded-xl border-emerald-200 bg-emerald-50/60 p-4"}>
                    {homeWarnings.warnings.length ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                                <AlertTriangle className="h-4 w-4" /> Home catalog warnings
                            </div>
                            <ul className="space-y-1 text-sm text-amber-900">
                                {homeWarnings.warnings.map((warning) => (
                                    <li key={warning.text} className="leading-snug">• {warning.text}</li>
                                ))}
                            </ul>
                            {canEditCatalog && (
                                <div>
                                    <a href="/settings" className="text-sm font-medium text-[#1E6FA8] hover:underline" data-testid="step-home-open-catalog-link">
                                        Open Settings → Home Catalog to fix this home
                                    </a>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                            <CircleCheck className="h-4 w-4" /> Catalog data complete
                        </div>
                    )}
                </Card>
            )}
{!home?.model_name && (
                <>
                    <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Pick from catalog</div>
                            <div className="text-xs text-slate-500">388 homes across 6 manufacturers</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Select value={manufacturer} onValueChange={(v) => { setManufacturer(v); setSeries("all"); }}>
                                <SelectTrigger data-testid="step-home-manufacturer-select"><SelectValue placeholder="Manufacturer" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All manufacturers</SelectItem>
                                    {manufacturers.map((m) => (
                                        <SelectItem key={m.manufacturer} value={m.manufacturer}>{m.manufacturer} ({m.total})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={series} onValueChange={setSeries} disabled={manufacturer === "all"}>
                                <SelectTrigger data-testid="step-home-series-select"><SelectValue placeholder="Series" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All series</SelectItem>
                                    {seriesList.map((s) => (
                                        <SelectItem key={s.name} value={s.name}>{s.name} ({s.count})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && loadHomes()}
                                    placeholder="Search model, manufacturer, or series…"
                                    className="pl-9"
                                    data-testid="step-home-search-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <Select value={bedsFilter} onValueChange={setBedsFilter}>
                                <SelectTrigger data-testid="step-home-beds-filter"><SelectValue placeholder="Bedrooms" /></SelectTrigger>
                                <SelectContent>{["all","1","2","3","4","5"].map((v)=><SelectItem key={v} value={v}>{v==="all"?"All bedrooms":`${v} bd`}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={bathsFilter} onValueChange={setBathsFilter}>
                                <SelectTrigger data-testid="step-home-baths-filter"><SelectValue placeholder="Bathrooms" /></SelectTrigger>
                                <SelectContent>{["all","1","1.5","2","2.5","3","3.5","4"].map((v)=><SelectItem key={v} value={v}>{v==="all"?"All bathrooms":`${v} ba`}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={homeTypeFilter} onValueChange={setHomeTypeFilter}>
                                <SelectTrigger data-testid="step-home-type-filter"><SelectValue placeholder="Home type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All home types</SelectItem>
                                    {homeTypes.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                                </SelectContent>
                            </Select>
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger data-testid="step-home-sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="price_asc">Price: Low to high</SelectItem>
                                    <SelectItem value="price_desc">Price: High to low</SelectItem>
                                    <SelectItem value="sqft_asc">Sqft: Low to high</SelectItem>
                                    <SelectItem value="sqft_desc">Sqft: High to low</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button variant="ghost" onClick={() => { setBedsFilter("all"); setBathsFilter("all"); setHomeTypeFilter("all"); setSortBy("price_asc"); setQ(""); }} className="w-full">Clear filters</Button>
                        </div>
                        <div className="flex justify-between items-center">
                            <Button variant="outline" onClick={loadHomes} data-testid="step-home-search-button">Search</Button>
                            <button
                                type="button"
                                className="text-sm text-[#1E6FA8] hover:underline"
                                onClick={() => setCustomMode(true)}
                                data-testid="step-home-custom-button"
                            >
                                Or enter custom home details →
                            </button>
                        </div>
                    </Card>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                        {filteredHomes.length === 0 && (
                            <Card className="p-8 text-center text-sm text-slate-500">No homes found. Try clearing filters.</Card>
                        )}
                        {filteredHomes.map((h) => (
                            <button
                                key={h.id}
                                type="button"
                                onClick={() => onSelect(h)}
                                className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 transition-colors duration-150 hover:border-[#1E6FA8]"
                                data-testid={`step-home-row-${h.id}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="font-medium text-slate-900 truncate">{h.model_name}</div>
                                        <div className="text-xs text-slate-500">
                                            {h.manufacturer} · {h.series} · {h.home_type || "—"} · {h.dimensions || "—"}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-500">
                                            {h.beds || "?"} bd · {h.baths || "?"} ba · {h.sqft ? `${h.sqft} sqft` : "— sqft"}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs text-slate-500">EHS Price</div>
                                        <div className="font-semibold tabular text-slate-900">{h.ehs_price ? money(h.ehs_price, { decimals: 0 }) : "—"}</div>
                                        {h.est_factory_cost && (
                                            <div className="text-xs text-slate-500 mt-0.5">Cost: {money(h.est_factory_cost, { decimals: 0 })}</div>
                                        )}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}

            {customMode && !home?.model_name && (
                <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900">Custom home details</div>
                        <Button variant="outline" size="sm" onClick={() => setCustomMode(false)}>Back to catalog</Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Input placeholder="Manufacturer" onChange={(e) => onSelect({
                            id: null, manufacturer: e.target.value, series: "", model_name: "", est_factory_cost: 0, ehs_price: 0
                        })} />
                    </div>
                    <div className="text-xs text-slate-500">Tip: switch back to catalog after entering manufacturer to refine pricing.</div>
                </Card>
            )}

            {home?.model_name && (
                <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Adjust pricing and calculator fields</div>
                        <div className="text-xs text-slate-500">
                            Dimensions feed skirting, gutters, block/tie-down, delivery sides, and other automatic calculations.
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Base home price (customer-facing)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={basePrice || 0}
                                onChange={(e) => update({ base_price: parseNumber(e.target.value) })}
                                disabled={!canEditQuotePricing}
                                title={quotePricingDisabledTitle}
                                data-testid="step-home-base-price-input"
                            />
                            <div className="mt-1 text-xs text-slate-500">
                                Calculated EHS price: {money(calculatedEhsPrice || 0, { decimals: 2 })}{priceOverridden ? " (manual override active)" : ""}
                            </div>
                            {hasPendingPriceOverride && (
                                <div className="mt-2 space-y-1">
                                    <Label className="text-xs uppercase tracking-wide text-slate-500">Price change note (optional)</Label>
                                    <Input
                                        value={overrideReason || ""}
                                        onChange={(e) => update({ ehs_price_override_reason: e.target.value })}
                                        disabled={!canEditQuotePricing}
                                        title={quotePricingDisabledTitle}
                                        placeholder="Optional context for this quote-specific price change"
                                        data-testid="step-home-override-reason-input"
                                    />
                                </div>
                            )}
                            {(priceOverridden || hasPendingPriceOverride) && (
                                <Button type="button" variant="outline" size="sm" className="mt-2" disabled={!canEditQuotePricing} title={quotePricingDisabledTitle} onClick={() => update({ reset_ehs_price_override: true, ehs_price_override: { active: false }, ehs_price_override_reason: "", base_price: calculatedEhsPrice || basePrice || 0 })}>
                                    Reset to Calculated EHS Price
                                </Button>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Unit factory cost (internal)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={factoryCost || 0}
                                onChange={(e) => update({ factory_cost: parseNumber(e.target.value) })}
                                disabled={!canEditQuotePricing}
                                title={quotePricingDisabledTitle}
                                data-testid="step-home-factory-cost-input"
                            />
                            <div className="mt-1 text-xs text-slate-500">MSRP: {money(msrpCalculated || 0, { decimals: 2 })}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Calculator dimensions</Label>
                            <Input
                                value={home.dimensions || ""}
                                onChange={(e) => updateSelectedHome({ dimensions: e.target.value })}
                                placeholder="Example: 28 x 60"
                                data-testid="step-home-dimensions-input"
                            />
                            <div className="mt-1 text-xs text-slate-500">
                                {perimeter ? `Perimeter for skirting: ${perimeter} linear ft` : "Required for skirting/gutter calculations."}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Square footage</Label>
                            <Input
                                type="number"
                                step="1"
                                value={home.sqft || ""}
                                onChange={(e) => updateSelectedHome({ sqft: parseNumber(e.target.value) })}
                                placeholder="Sq ft"
                                data-testid="step-home-sqft-input"
                            />
                            <div className="mt-1 text-xs text-slate-500">Feeds AC tonnage calculation.</div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Home type</Label>
                            <Select value={home.home_type || ""} onValueChange={(v) => updateSelectedHome({ home_type: v, home_type_source: "manual_override" })}>
                                <SelectTrigger data-testid="step-home-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Single Wide">Single Wide</SelectItem>
                                    <SelectItem value="Double Wide">Double Wide</SelectItem>
                                    <SelectItem value="Triple Wide">Triple Wide</SelectItem>
                                    <SelectItem value="Multi-Section">Multi-Section</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="mt-1 text-xs text-slate-500">{homeTypeHelperText(home?.home_type_source)}</div>
                            <div className="mt-0.5 text-xs text-slate-500">Feeds block/tie-down and transport side assumptions.</div>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
}
